// Settings > Connections: pure helpers (id generator, status dot, texts). No imports, so the
// frontend tests can compile it.

// The filter box over the connection list appears only above this many connections.
export const CONNECTIONS_FILTER_MIN = 9;

export type DotColor = "green" | "amber" | "red" | "grey";

export interface StatusDot {
  color: DotColor;
  label: string;
}

// The Health page's liveness verdict (lib/definitions.ts livenessSeverity), passed in so this
// module stays import-free: healthy = connected and heard recently, stale = connected but
// quiet, unhealthy = not connected, or connected and silent for a long time.
export type Liveness = "healthy" | "stale" | "unhealthy";

// Green only for a connection that is connected AND recently heard; connected but quiet is
// amber, connected but silent is red; connecting or reconnecting = amber; error or
// disconnected = red; off = grey. An enabled connection the backend does not report at all
// counts as disconnected. Without a liveness verdict a connected connection counts as healthy.
export function statusDot(enabled: boolean, state: string | null | undefined, liveness?: Liveness): StatusDot {
  if (!enabled) return { color: "grey", label: "Off" };
  switch (state) {
    case "connected":
      if (liveness === "stale") return { color: "amber", label: "Connected, Quiet" };
      if (liveness === "unhealthy") return { color: "red", label: "Connected, Silent" };
      return { color: "green", label: "Connected" };
    case "connecting":
      return { color: "amber", label: "Connecting" };
    case "reconnecting":
      return { color: "amber", label: "Reconnecting" };
    case "error":
      return { color: "red", label: "Error" };
    default:
      return { color: "red", label: "Disconnected" };
  }
}

// "2 min ago". null / unparseable = "not heard yet".
export function lastHeardText(ts: string | null | undefined, now: number = Date.now()): string {
  if (!ts) return "Not heard yet";
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return "Not heard yet";
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 5) return "Just now";
  if (sec < 60) return `${sec} s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h ago`;
  return `${Math.round(sec / 86400)} d ago`;
}

// "18 s", "3 min", "5 h", "2 d": how long ago a time was, without the word "ago" (for "Connected for 18 s").
export function sinceText(ts: string | null | undefined, now: number = Date.now()): string | null {
  if (!ts) return null;
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return null;
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h`;
  return `${Math.round(sec / 86400)} d`;
}

export interface StatusInput {
  enabled: boolean;
  state: string | null | undefined;
  lastPacketAt?: string | null;
  lastError?: string | null;
  lastErrorAt?: string | null;
  lastConnectedAt?: string | null;
  reconnectAttempts?: number | null;
  nextRetryAt?: string | null;
  // Store What It Hears is off: the connection never receives packets, so it is judged by its
  // last stats poll instead.
  statsOnly?: boolean;
  lastStatsAt?: string | null;
}

// The Health-page verdict for a connection. `severity` is livenessSeverity from definitions.ts.
// A stats-only connection never receives packets, so it is judged by its last stats poll when
// the health payload has one, and otherwise plain connected = healthy.
export function connectionLiveness(
  statsOnly: boolean,
  state: string | null | undefined,
  lastPacketAt: string | null | undefined,
  lastStatsAt: string | null | undefined,
  severity: (state: string | null | undefined, lastHeard: string | null | undefined) => Liveness,
): Liveness {
  if (!statsOnly) return severity(state, lastPacketAt);
  if (lastStatsAt) return severity(state, lastStatsAt);
  return state === "connected" ? "healthy" : "unhealthy";
}

// "in 12 s" style wait until an ISO time; "now" once it has passed.
export function retryInText(nextRetryAt: string | null | undefined, now: number = Date.now()): string | null {
  if (!nextRetryAt) return null;
  const t = Date.parse(nextRetryAt);
  if (Number.isNaN(t)) return null;
  const sec = Math.ceil((t - now) / 1000);
  return sec <= 0 ? "now" : `in ${sec} s`;
}

// "Turned Off" | "Connected for 3 h. Last packet 2 min ago." | "Connected for 18 s. Last packet just now." |
// "Reconnecting, attempt 3, next try in 12 s. Last connected 5 min ago. Last error: timed out (1 min ago)."
// The last error is shown only when there is one.
export function statusLine(input: StatusInput, now: number = Date.now()): string {
  if (!input.enabled) return "Turned Off";
  const parts: string[] = [];
  const heardRaw = input.lastPacketAt && lastHeardText(input.lastPacketAt, now) !== "Not heard yet" ? lastHeardText(input.lastPacketAt, now) : null;
  // Mid-sentence: "Last packet just now."
  const heard = heardRaw === "Just now" ? "just now" : heardRaw;
  const upFor = input.state === "connected" ? sinceText(input.lastConnectedAt, now) : null;
  const connectedHead = upFor ? `Connected for ${upFor}.` : "Connected.";
  switch (input.state) {
    case "connected":
      if (input.statsOnly) {
        const stats = input.lastStatsAt && lastHeardText(input.lastStatsAt, now) !== "Not heard yet" ? lastHeardText(input.lastStatsAt, now) : null;
        const statsHeard = stats === "Just now" ? "just now" : stats;
        parts.push(statsHeard ? `${connectedHead} Stats only, last stats ${statsHeard}.` : `${connectedHead} Stats only.`);
      } else {
        parts.push(heard ? `${connectedHead} Last packet ${heard}.` : `${connectedHead} No packets yet.`);
      }
      break;
    case "connecting":
      parts.push("Connecting.");
      break;
    case "reconnecting":
    case "error": {
      const attempts = input.reconnectAttempts && input.reconnectAttempts > 0 ? `, attempt ${input.reconnectAttempts}` : "";
      const next = retryInText(input.nextRetryAt, now);
      parts.push(`Reconnecting${attempts}${next ? `, next try ${next}` : ""}.`);
      if (heard) parts.push(`Last packet ${heard}.`);
      break;
    }
    default:
      parts.push("Disconnected.");
      if (heard) parts.push(`Last packet ${heard}.`);
  }
  if (!upFor && input.lastConnectedAt && lastHeardText(input.lastConnectedAt, now) !== "Not heard yet") {
    parts.push(`Last connected ${lastHeardText(input.lastConnectedAt, now)}.`);
  }
  const err = input.lastError && input.lastError.trim() ? input.lastError.trim() : "";
  if (err) {
    const when = input.lastErrorAt && lastHeardText(input.lastErrorAt, now) !== "Not heard yet" ? ` (${lastHeardText(input.lastErrorAt, now)})` : "";
    parts.push(`Last error: ${err}${when}.`);
  }
  return parts.join(" ");
}

// The internal id for a new connection: lower case, every run of non-alphanumerics becomes one
// underscore, trimmed; "_2" (then "_3", ...) is appended while the id is taken.
export function generateConnectionId(name: string, taken: readonly string[]): string {
  let base = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!base) base = "connection";
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function addressText(host: unknown, port: unknown): string {
  const h = typeof host === "string" ? host.trim() : "";
  const p = typeof port === "number" && Number.isFinite(port) && port > 0 ? String(port) : "";
  if (h && p) return `${h}:${p}`;
  return h || p || "No address";
}

export interface ConnectionSortable {
  id: string;
  label?: unknown;
}

export function connectionName(c: ConnectionSortable): string {
  return typeof c.label === "string" && c.label.trim() ? c.label.trim() : c.id;
}

// Sorted by name, case-insensitive, then by id.
export function sortConnections<T extends ConnectionSortable>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => {
    const an = connectionName(a).toLowerCase();
    const bn = connectionName(b).toLowerCase();
    return an < bn ? -1 : an > bn ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

// Null when the add form is complete, else the sentence to show.
export function addConnectionError(name: string, host: string, port: string): string | null {
  if (!name.trim()) return "Enter a name.";
  if (!host.trim()) return "Enter a host.";
  const p = Number(port);
  if (!port.trim() || !Number.isInteger(p) || p < 1 || p > 65535) return "Enter a port between 1 and 65535.";
  return null;
}

export interface AppliedLike {
  path: string;
  result: string;
  detail?: string;
}

// "Saved and applied." or "Saved, but could not connect: <reason>" from this connection's applied entry.
export function connectionSaveText(applied: readonly AppliedLike[] | null | undefined, id: string): string {
  const bad = (applied ?? []).filter((a) => a && a.result === "needs_attention" && (a.path === `transports.${id}` || !a.path));
  if (bad.length === 0) return "Saved and applied.";
  const reason = bad.map((a) => (a.detail && a.detail.trim() ? a.detail.trim() : "no reason given")).join(" ");
  return `Saved, but could not connect: ${reason}`;
}

export function hasUnsavedChanges(baseline: Record<string, unknown>, current: Record<string, unknown>): boolean {
  return JSON.stringify(baseline) !== JSON.stringify(current);
}

// The fields of `current` that differ from `baseline`, for the PATCH body.
export function changedFields(paths: readonly string[], baseline: Record<string, unknown>, current: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of paths) {
    if (JSON.stringify(baseline[p]) !== JSON.stringify(current[p])) out[p] = current[p] === undefined ? null : current[p];
  }
  return out;
}

// The trust tier a new connection starts with: MeshCore packets carry no signature we can
// verify, so MeshCore starts unsigned; everything else starts as our radio over the air.
export function defaultTrustTier(kind: string): string {
  return kind === "meshcore_tcp" ? "first_party_unsigned" : "first_party";
}

export interface TrustTier {
  value: string;
  label: string;
  help: string;
}

// What each tier means in the backend (ingest/provenance.py, trust.py): over the air = our own
// radio received it by RF; local network = it reached our radio over the LAN (Meshtastic
// multicast UDP), neither RF nor the internet; unsigned = our own radio, but the protocol
// (MeshCore) gives no signature to prove the content was not altered.
export const CONNECTION_TRUST_TIERS: readonly TrustTier[] = [
  {
    value: "first_party",
    label: "Our Radio, Over The Air",
    help: "Packets our own radio heard over RF. Counts as first-hand proof.",
  },
  {
    value: "first_party_local_network",
    label: "Our Radio, Local Network",
    help: "Packets that reached our radio over the local network (Meshtastic multicast UDP) instead of over the air. Ours, but not an RF reception. Pick this only for a connection that carries LAN-relayed traffic rather than a radio's own reception.",
  },
  {
    value: "first_party_unsigned",
    label: "Our Radio, Unsigned",
    help: "Packets from our own radio whose content we cannot prove was not altered. Pick this for any MeshCore radio or virtual node; MeshCore packets carry no signature we can verify, so they do not count as first-hand proof.",
  },
];

// The "which one to pick" sentence is per radio type: the Meshtastic advice is shown only on a Meshtastic connection.
const TRUST_PICK_HINT: Record<string, Record<string, string>> = {
  first_party: { meshtastic_tcp: "Pick this for your own Meshtastic radio, direct or through a MeshMonitor virtual node of it." },
};

export function trustTierHelp(value: string, kind: string): string {
  const base = CONNECTION_TRUST_TIERS.find((t) => t.value === value)?.help ?? "";
  const hint = TRUST_PICK_HINT[value]?.[kind];
  return hint ? `${base} ${hint}` : base;
}

export function trustTierLabel(value: string): string {
  return CONNECTION_TRUST_TIERS.find((t) => t.value === value)?.label ?? value;
}

// Meshtastic: the configured tier has no effect, each packet's tier comes from how it arrived.
export const META_TRUST_SENTENCE = "Trust is set per packet from how it arrived: over the air, local network, or MQTT.";

// Add form, under Radio Type. Meshtastic: the per-packet sentence. MeshCore: the tier.
export function defaultTrustText(kind: string): string {
  if (kind !== "meshcore_tcp") return META_TRUST_SENTENCE;
  return `Trusted As: ${trustTierLabel(defaultTrustTier(kind))} (change after adding)`;
}

// The What We Trust It For picker is only for connections whose configured tier counts (MeshCore).
export function showsTrustPicker(kind: string): boolean {
  return kind === "meshcore_tcp";
}

export const KIND_MESHCORE = "meshcore_tcp";

// The port a new connection of this type starts with.
export function defaultPort(kind: string): number {
  return kind === KIND_MESHCORE ? 5000 : 4403;
}

export function portHelp(kind: string): string {
  return kind === KIND_MESHCORE
    ? "A MeshMonitor virtual node uses the port set on that virtual node (often 5000). An OpenHop companion uses the port set in OpenHop (often 5051)."
    : "Usually 4403 for a radio's TCP API. A MeshMonitor virtual node uses the port set on it (often 4404).";
}

// The name of another connection that already uses this host and port (host compared without
// case or surrounding spaces), or null. `exceptId` skips the connection being edited.
export function duplicateAddress(
  others: readonly { id: string; label?: unknown; host?: unknown; port?: unknown }[],
  host: string,
  port: number,
  exceptId?: string,
): string | null {
  const h = host.trim().toLowerCase();
  if (!h || !Number.isFinite(port)) return null;
  for (const o of others) {
    if (o.id === exceptId) continue;
    const oh = typeof o.host === "string" ? o.host.trim().toLowerCase() : "";
    if (oh === h && o.port === port) return connectionName(o);
  }
  return null;
}

export interface TestResult {
  ok: boolean;
  detail: string;
}

// The line under the Test button. A request that failed outright reads as a failed test.
export function testResultText(r: TestResult | null | undefined): string {
  if (!r) return "";
  const d = r.detail && r.detail.trim() ? r.detail.trim() : r.ok ? "The connection worked." : "The connection failed.";
  return r.ok ? d : `Test failed. ${d}`;
}

export type SaveOutcome =
  | { state: "connected" }
  | { state: "connecting" }
  | { state: "failed"; error: string };

// The line shown after a save once the connection has been watched for a few seconds.
export function saveOutcomeText(o: SaveOutcome): string {
  if (o.state === "connected") return "Saved and applied. Connected.";
  if (o.state === "connecting") return "Saved and applied. Connecting…";
  return `Saved, but could not connect: ${o.error && o.error.trim() ? o.error.trim() : "no reason given"}`;
}

// How a connection looks once the save has been watched for a while (8 s in the panel):
// connected = done; still connecting or reconnecting with no error = "Connecting…"; anything
// else (an error, or the backend not reporting it) = could not connect, with the last error.
export function settleOutcome(h: { state: string; last_error?: string | null } | null | undefined): SaveOutcome {
  if (h && h.state === "connected") return { state: "connected" };
  const err = h && h.last_error && h.last_error.trim() ? h.last_error.trim() : "";
  if (h && (h.state === "connecting" || h.state === "reconnecting") && !err) return { state: "connecting" };
  return { state: "failed", error: err || (h ? `the connection is ${h.state}` : "the connection did not start") };
}
